
import { Shop } from '@/types';
import { offlineDb, SyncableShop } from '@/db/offlineDb';
import OfflineLogger from '@/utils/offlineLogger';
import { syncService } from '@/services/sync.service';
import { v4 as uuid } from 'uuid';
import { getValidUserId } from '@/utils/validation';

// Helper functions to convert between Shop and SyncableShop
const convertToSyncableShop = (shop: Shop): SyncableShop => ({
  id: shop.id,
  user_id: shop.user_id || '',
  shop_name: shop.shop_name, // ✅ Corretto: shop_name
  address: shop.address || null,
  latitude: shop.latitude || null,
  longitude: shop.longitude || null,
  shop_logo: shop.shop_logo || null, // ✅ Corretto: shop_logo
  created_at: shop.created_at || new Date().toISOString(),
  updated_at: shop.updated_at || new Date().toISOString(),
  _syncStatus: 'pending' as const,
  _lastModified: Date.now()
});

const convertToShop = (syncableShop: SyncableShop): Shop => ({
  id: syncableShop.id,
  user_id: syncableShop.user_id,
  shop_name: syncableShop.shop_name, // ✅ Corretto: shop_name
  address: syncableShop.address,
  latitude: syncableShop.latitude,
  longitude: syncableShop.longitude,
  shop_logo: syncableShop.shop_logo, // ✅ Corretto: shop_logo
  created_at: syncableShop.created_at,
  updated_at: syncableShop.updated_at
});

export class ShopRepository {
  protected tableName = 'user_shops';
  protected dbTable = offlineDb.shops;
  
  async create(shop: Omit<Shop, "id" | "user_id" | "created_at" | "updated_at">): Promise<Shop> {
    try {
      const id = uuid();
      const now = new Date().toISOString();
      const userId = getValidUserId();
      if (!userId) throw new Error('Invalid user id');
      
      const newShop: Shop = {
        ...shop,
        id,
        user_id: userId,
        created_at: now,
        updated_at: now
      };
      
      const syncableShop = convertToSyncableShop(newShop);
      await this.dbTable.put(syncableShop);
      
      // Add to sync queue for online sync later
      await syncService.addToSyncQueue(
        'shops',
        'create',
        id,
        {
          ...syncableShop,
          _syncStatus: undefined,
          _lastModified: undefined
        }
      );

      OfflineLogger.info('ShopRepository.create', `Created shop: ${id}`);
      return newShop;
    } catch (error) {
      OfflineLogger.error('ShopRepository.create', error);
      throw error;
    }
  }

  async getAll(): Promise<Shop[]> {
    try {
      const shops = await this.dbTable
        .where('_syncStatus')
        .notEqual('deleted')
        .toArray();
        
      return shops.map(convertToShop);
    } catch (error) {
      OfflineLogger.error('ShopRepository.getAll', error);
      return [];
    }
  }
  
  async update(id: string, updates: Partial<Shop>): Promise<Shop | null> {
    try {
      const updateData: Partial<SyncableShop> = {
        ...updates,
        updated_at: new Date().toISOString(),
        _syncStatus: 'pending',
        _lastModified: Date.now()
      };
      
      await this.dbTable.update(id, updateData);
      
      const updated = await this.dbTable.get(id);
      if (!updated) return null;
      
      // Add to sync queue
      await syncService.addToSyncQueue('shops', 'update', id, {
        ...updateData,
        _syncStatus: undefined,
        _lastModified: undefined
      });
      
      return convertToShop(updated);
    } catch (error) {
      OfflineLogger.error('ShopRepository.update', error);
      throw error;
    }
  }
  
  async delete(id: string): Promise<void> {
    try {
      await this.dbTable.update(id, {
        _syncStatus: 'deleted' as const,
        _lastModified: Date.now()
      });
      
      await syncService.addToSyncQueue('shops', 'delete', id);

      OfflineLogger.info('ShopRepository.delete', `Marked shop ${id} for deletion`);
    } catch (error) {
      OfflineLogger.error('ShopRepository.delete', error);
      throw error;
    }
  }

  async saveMany(shops: Shop[]): Promise<void> {
    try {
      const syncableShops = shops.map(shop => ({
        ...convertToSyncableShop(shop),
        _syncStatus: 'synced' as const
      }));
      
      await this.dbTable.bulkPut(syncableShops);
      OfflineLogger.info('ShopRepository.saveMany', `Saved ${shops.length} shops to local DB`);
    } catch (error) {
      OfflineLogger.error('ShopRepository.saveMany', error);
      throw error;
    }
  }
}

export const shopRepository = new ShopRepository();
